import { formatEmissionUnit } from "@/lib/emissionUnits";
import { getChartPalette } from "../theme";
import { formatLargeNumber } from "../units";
import { readableTextColor } from "./colors";
import type { ChartContext } from "./context";

export function buildCartesianOption(
  context: ChartContext,
): Record<string, unknown> {
  const {
    spec,
    chartType,
    data,
    isDark,
    width,
    translateNode,
    displayUnit,
    displayUnitLabel,
    unitText,
    axisText,
    ct,
    base,
  } = context;
  // === CARTESIAN (LINE / AREA / BAR) ===
  const secondary = data.secondaryAxis;
  const secondaryIndex = secondary?.datasetIndex ?? -1;
  const secondaryUnit = formatEmissionUnit(secondary?.unit);
  const secondaryUnitText = (val: number): string =>
    secondaryUnit
      ? `${formatLargeNumber(val)} ${secondaryUnit}`
      : formatLargeNumber(val);

  const axisTooltip = {
    trigger: "axis" as const,
    confine: true,
    backgroundColor: ct.bgElevated,
    borderColor: ct.border,
    formatter: (params: unknown) => {
      const items = Array.isArray(params) ? params : [params];
      const list = items as Array<{
        seriesIndex: number;
        seriesName: string;
        value: number | number[] | null;
        axisValueLabel?: string;
        name?: string;
      }>;
      const name = list[0]?.name ?? list[0]?.axisValueLabel ?? "";
      const body = list
        .filter((p) => p.value != null)
        .map((p) => {
          const raw = typeof p.value === "number" ? p.value : p.value![1];
          const text =
            p.seriesIndex === secondaryIndex
              ? secondaryUnitText(raw)
              : unitText(raw);
          return width < 400
            ? `${translateNode(p.seriesName)}:\n${text}`
            : `${translateNode(p.seriesName)}: ${text}`;
        })
        .join("\n");
      const label = translateNode(name);
      return label ? `${label}\n${body}` : body;
    },
  };

  const hasTypeOverride = data.datasets.some((ds) => ds.type);
  const eSeriesType =
    chartType === "area" ? "line" : chartType === "bar" ? "bar" : "line";
  const manyLabels = data.labels.length > 8;
  const xLabelRotation = spec.xLabelRotation ?? (manyLabels ? 45 : 0);
  const isHorizontal = data.horizontal === true && chartType === "bar";
  const compact = width < 400;
  const horizontalLabelWidth = Math.min(
    140,
    Math.max(60, Math.round(width * 0.3)),
  );
  const xAxisLabelMaxLength = data.xAxisLabelMaxLength;
  const xAxisLabelFormatter = (source: string) => {
    const value = translateNode(source);
    return xAxisLabelMaxLength && value.length > xAxisLabelMaxLength
      ? `${value.slice(0, xAxisLabelMaxLength - 3)}...`
      : value;
  };

  const primaryYAxis = {
    type: "value",
    axisLabel: { color: ct.secondary, fontSize: 11, formatter: axisText },
    splitLine: { lineStyle: { color: ct.lightBorder } },
    ...(displayUnit
      ? {
          name: displayUnitLabel,
          nameLocation: compact ? ("end" as const) : ("middle" as const),
          // Leave room for formatted ticks such as 140.0K when units change.
          nameGap: compact ? 12 : 52,
          ...(compact ? { nameRotate: 0 } : {}),
          nameTextStyle: {
            color: ct.secondary,
            fontSize: 13,
            fontWeight: 400,
            ...(compact ? { align: "left" as const } : {}),
          },
        }
      : {}),
  };

  return {
    ...base,
    tooltip: axisTooltip,
    xAxis: isHorizontal
      ? {
          type: "value",
          splitNumber: compact ? 2 : 5,
          axisLabel: {
            color: ct.secondary,
            fontSize: 11,
            formatter: axisText,
            hideOverlap: true,
          },
          splitLine: { lineStyle: { color: ct.lightBorder } },
          ...(displayUnit
            ? {
                name: displayUnitLabel,
                nameLocation: "middle" as const,
                nameGap: 40,
                nameTextStyle: {
                  color: ct.secondary,
                  fontSize: 13,
                  fontWeight: 400,
                },
              }
            : {}),
        }
      : {
          type: "category",
          data: data.labels,
          axisLabel: {
            color: ct.secondary,
            fontSize: 11,
            interval: compact ? "auto" : 0,
            rotate: xLabelRotation,
            hideOverlap: true,
            ...(xAxisLabelFormatter ? { formatter: xAxisLabelFormatter } : {}),
          },
          axisLine: { lineStyle: { color: ct.border } },
        },
    yAxis: isHorizontal
      ? {
          type: "category",
          data: data.labels,
          inverse: true,
          axisLabel: {
            color: ct.secondary,
            fontSize: 11,
            interval: 0,
            width: horizontalLabelWidth,
            overflow: "truncate",
            margin: 12,
            formatter: translateNode,
          },
          axisLine: { lineStyle: { color: ct.border } },
        }
      : secondary
        ? [
            primaryYAxis,
            {
              type: "value",
              name: formatEmissionUnit(secondary.name),
              nameLocation: "middle",
              nameGap: 40,
              nameTextStyle: {
                color: ct.secondary,
                fontSize: 13,
                fontWeight: 400,
              },
              axisLabel: { color: ct.secondary, fontSize: 11 },
              splitLine: { show: false },
            },
          ]
        : primaryYAxis,
    grid: {
      left: isHorizontal ? horizontalLabelWidth + 16 : compact ? 32 : 60,
      right: secondary
        ? compact
          ? 40
          : 70
        : data.valueLabels && !compact
          ? 70
          : 16,
      bottom: isHorizontal ? 60 : xLabelRotation > 0 ? 70 : 40,
      top: spec.showTitle === false ? 35 : 65,
      // Long ranked labels must not shrink the plot area to zero width.
      containLabel: !isHorizontal,
    },
    series: data.datasets.map((ds, i) => {
      const isOverlay = ds.type != null;
      const numericValues = ds.values as number[];
      const requestedType = ds.type ?? chartType;
      const actualType = requestedType === "area" ? "line" : requestedType;
      const seriesColor = isOverlay
        ? ct.combo
        : getChartPalette(isDark, data.datasets.length)[i];
      const resolvedColor = ds.color ?? seriesColor;
      const seriesData =
        eSeriesType === "bar" && data.itemColors?.length
          ? numericValues.map((value, index) => ({
              value,
              itemStyle: {
                color: data.itemColors?.[index] ?? resolvedColor,
              },
            }))
          : numericValues;
      return {
        name: ds.label,
        type: actualType,
        data: seriesData,
        color: resolvedColor,
        ...(secondary && i === secondaryIndex ? { yAxisIndex: 1 } : {}),
        ...(requestedType === "area"
          ? { areaStyle: { color: resolvedColor, opacity: 0.14 } }
          : {}),
        ...(eSeriesType === "bar" && data.stacked && !isOverlay
          ? { stack: "total" }
          : {}),
        ...(eSeriesType === "bar" && !data.stacked
          ? {
              itemStyle: {
                borderRadius: isHorizontal ? [0, 3, 3, 0] : [3, 3, 0, 0],
              },
            }
          : {}),
        ...(eSeriesType === "bar" && hasTypeOverride
          ? { connectNulls: true }
          : {}),
        ...(eSeriesType === "bar" && data.valueLabels
          ? {
              label: {
                // Small ranked bars can be narrower than their value labels.
                // The tooltip and accessible summary retain the full values.
                show: !isHorizontal || !compact,
                position: isHorizontal ? "right" : "insideTop",
                color: isHorizontal
                  ? ct.secondary
                  : readableTextColor(resolvedColor),
                fontSize: 11,
                formatter: (p: { value: number }) =>
                  p.value ? unitText(p.value) : "",
              },
            }
          : {}),
        ...(actualType === "line"
          ? {
              lineStyle: {
                width: ds.lineStyle?.width ?? 2,
                type: ds.lineStyle?.type ?? "solid",
              },
              symbol: "circle",
              symbolSize: 4,
            }
          : {}),
      };
    }),
  };
}
